import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiGift, FiPercent, FiStar, FiClock, FiUsers, FiDollarSign } = FiIcons;

const LeadCaptureIncentives = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const incentives = [
    {
      icon: FiGift,
      title: "$500 Exclusive Bonus",
      description: "Available only to online leads",
      highlight: "Limited Time"
    },
    {
      icon: FiPercent,
      title: "VIP Pricing Access",
      description: "See dealer-only pricing",
      highlight: "Exclusive"
    },
    {
      icon: FiStar,
      title: "Priority Service",
      description: "Skip the wait, get immediate attention",
      highlight: "Premium"
    },
    {
      icon: FiClock,
      title: "24hr Price Lock",
      description: "Lock in today's pricing",
      highlight: "Guaranteed"
    }
  ];

  const urgencyFactors = [
    "🔥 Only 12 hours left for this month's incentives",
    "⚡ 3 people submitted leads in the last hour",
    "🎯 Limited inventory on popular models",
    "💰 Interest rates may increase next month"
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    // Here you would typically send the data to your CRM/backend
    setTimeout(() => {
      alert('Success! Check your email for your VIP pricing and exclusive offers. A specialist will contact you within 15 minutes.');
    }, 1000);
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-green-900 to-green-800 rounded-lg p-6 border border-green-600 text-center"
      >
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiStar} className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Welcome to VIP Status!</h3>
        <p className="text-green-100 mb-4">
          Your exclusive offers are being prepared. Check your email in the next few minutes.
        </p>
        <div className="bg-green-800 rounded-lg p-4">
          <p className="text-green-200 text-sm">
            🎉 You've unlocked: $500 bonus, VIP pricing, and priority service
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-6 border-2 border-audi-red"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">
          🎁 Unlock Exclusive VIP Benefits
        </h3>
        <p className="text-gray-300">
          Get instant access to dealer pricing and exclusive incentives
        </p>
      </div>

      {/* Incentives Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {incentives.map((incentive, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.05 }}
            className="bg-gray-700 rounded-lg p-4 text-center border border-gray-600"
          >
            <div className="w-10 h-10 bg-audi-red rounded-lg flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={incentive.icon} className="w-5 h-5 text-white" />
            </div>
            <div className="text-xs font-bold text-audi-red mb-1">{incentive.highlight}</div>
            <div className="font-semibold text-white text-sm">{incentive.title}</div>
            <div className="text-gray-300 text-xs">{incentive.description}</div>
          </motion.div>
        ))}
      </div>

      {/* Urgency Factors */}
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
        <div className="space-y-2">
          {urgencyFactors.map((factor, index) => (
            <div key={index} className="text-red-200 text-sm flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
              {factor}
            </div>
          ))}
        </div>
      </div>

      {/* Lead Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Full Name *"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            required
          />
          <input
            type="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="tel"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            required
          />
          <select
            value={formData.interest}
            onChange={(e) => setFormData({...formData, interest: e.target.value})}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            required
          >
            <option value="">I'm interested in... *</option>
            <option value="buying">Buying a vehicle</option>
            <option value="leasing">Leasing a vehicle</option>
            <option value="trading">Trading my current vehicle</option>
            <option value="browsing">Just browsing for now</option>
          </select>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          className="w-full bg-gradient-to-r from-audi-red to-red-600 hover:from-red-700 hover:to-red-800 text-white py-4 px-6 rounded-lg font-bold text-lg transition-all duration-200 flex items-center justify-center"
        >
          <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2" />
          Unlock My VIP Benefits Now
        </motion.button>
      </form>

      {/* Trust Elements */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-center space-x-4 text-gray-400 text-xs">
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiUsers} className="w-3 h-3" />
            <span>No spam, ever</span>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-3 h-3" />
            <span>15-min response time</span>
          </div>
        </div>
        <p className="text-center text-gray-500 text-xs">
          By submitting, you agree to receive communications about exclusive offers. Unsubscribe anytime.
        </p>
      </div>
    </motion.div>
  );
};

export default LeadCaptureIncentives;